import React from 'react'
import PropTypes from 'prop-types'
import GatsbyImage from 'gatsby-image'
import Link from 'gatsby-link'
import lastIndexOf from 'lodash/lastIndexOf'
import withStyles from '@material-ui/core/styles/withStyles'
import Button from '@material-ui/core/Button'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import Divider from 'components/Divider'
import Tags from 'components/Tags'
import ShareBar from 'components/ShareBar'
import { ArticleMeta } from 'components/Meta'
import { pageStyle } from 'config/theme'

const styles = (theme) => ({
  content: {
    ...pageStyle(theme),
    margin: 0,
    padding: 10,
    width: 700,
    [theme.breakpoints.down('sm')]: {
      width: '90%',
    },
  },
  thumbnail: {
    width: '100%',
    height: 430,
    objectFit: 'cover',
    [theme.breakpoints.down('sm')]: {
      height: '100%',
    },
  },
  downloadBtn: {
    color: '#fff',
    background: theme.palette.text.review,
    '&:hover': {
      background: theme.palette.primary.main,
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.85em',
    },
  },
  title: {
    fontWeight: 600,
    color: theme.palette.primary.main,
    paddingTop: 10,
    [theme.breakpoints.down('xs')]: {
      fontSize: '1.4em',
    },
  },
  customerName: {
    fontSize: '1.1em',
    color: theme.palette.text.soft,
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.9em',
    },
  },
  paginateButton: {
    textTransform: 'none',
    background: theme.palette.primary.dark,
    transition: 'color 0.5s, background 0.5s',
    color: '#fff',
    margin: `0 ${theme.spacing.unit}px`,
    padding: 'auto 25px',
    borderRadius: theme.spacing.unit * 5,
    boxShadow: 'none',
    '&:hover': {
      color: theme.palette.primary.dark,
      background: theme.palette.borders.ghost,
    },
    [theme.breakpoints.down('xs')]: {
      width: '100%',
      fontSize: '0.8em',
    },
  },
  tagsContainer: {
    minHeight: 35,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
})

const ProductCustomerReviewTemplate = ({
  classes,
  theme,
  data,
  pathContext,
}) => {
  const { reviewBody } = pathContext
  const { customerReview, file } = data
  const baseUrl = 'https://healthfulinspired.com'

  const datePublished = new Date(customerReview.frontmatter.date).toISOString()
  const dateModified = file.modifiedTime
  const downloads = { pdf: [], doc: [], docx: [] } // to be updated

  if (customerReview.frontmatter.downloads) {
    customerReview.frontmatter.downloads.reduce((acc, val) => {
      const ext = val.slice(lastIndexOf(val, '.') + 1)
      if (ext === 'pdf') {
        acc.pdf.push(val)
      }
      if (ext === 'doc') {
        acc.doc.push(val)
      }
      if (ext === 'docx') {
        acc.docx.push(val)
      }
      return acc
    }, downloads)
  }

  return (
    <Grid
      id="productCustomerReviewPage"
      spacing={24}
      justify="center"
      alignItems="center"
      style={{ padding: '20px 0' }}
      container
    >
      <ArticleMeta
        title={customerReview.frontmatter.title}
        shortDescription={customerReview.frontmatter.shortDescription}
        thumbnail={customerReview.frontmatter.thumbnail}
        genre="health"
        tags={customerReview.frontmatter.tags}
        wordCount={customerReview.wordCount.words}
        datePublished={datePublished}
        dateModified={dateModified}
        articleBody={reviewBody}
        excerpt={customerReview.excerpt}
        path={customerReview.fields.slug}
      />
      <Grid className={classes.content} item>
        <Typography
          variant="display1"
          align="center"
          classes={{ display1: classes.title }}
        >
          {customerReview.frontmatter.title}
        </Typography>
        {customerReview.frontmatter.downloads && (
          <div style={{ textAlign: 'center', paddingBottom: 15 }}>
            {downloads.pdf.length &&
              downloads.pdf.map((url) => (
                <a key={url} href={url} target="_blank">
                  <Button
                    className={classes.downloadBtn}
                    size="small"
                    variant="raised"
                  >
                    Available in PDF
                  </Button>
                </a>
              ))}
          </div>
        )}
        <Typography align="center" className={classes.customerName}>
          Written by customer:{' '}
          <span style={{ color: '#772c4f' }}>
            <b>{customerReview.frontmatter.customerName}</b>
          </span>
        </Typography>
        <Typography
          align="center"
          style={{
            color: theme.palette.text.light,
            fontStyle: 'italic',
            paddingBottom: 15,
          }}
        >
          {customerReview.timeToRead} min read
        </Typography>
        <img
          className={classes.thumbnail}
          src={
            customerReview.frontmatter.customerPhoto ||
            customerReview.frontmatter.thumbnail
          }
          alt={customerReview.frontmatter.title}
          title={customerReview.frontmatter.title}
        />
        <ShareBar
          baseUrl={baseUrl}
          url={`${baseUrl}${customerReview.fields.slug}`}
          thumbnail={customerReview.frontmatter.thumbnail}
          title={customerReview.frontmatter.title}
          excerpt={customerReview.excerpt}
          tags={customerReview.frontmatter.tags}
        />

        <div
          className="markdown"
          dangerouslySetInnerHTML={{ __html: customerReview.html }}
        />
        <Divider />
        <div className={classes.tagsContainer}>
          <div>
            <Typography variant="body2">tags: &nbsp;</Typography>
          </div>
          <div>
            <Tags tags={customerReview.frontmatter.tags} />
          </div>
        </div>
      </Grid>
      <script async defer src="http://www.instagram.com/embed.js" />
      <style>{`
        div.markdown img {
          width: 100%;
          max-height: 350px;
          object-fit: cover;
          border: 1px solid ${theme.palette.borders.cloudier};
        }
        div.markdown {
          color: ${theme.palette.text.review};
        }
        table {
          border: 1px solid ${theme.palette.primary.main};
        }
        table th {
          background: ${theme.palette.primary.main};
          color: #fff;
          padding: 10px;
        }
        table td {
          border: 1px solid ${theme.palette.borders.cloud};
        }
        h1, h2, h3, h4, h5, h6 {
          line-height: 1em;
        }
        h1 {
          padding: 20px 0;
        }
        .instagram-media {
          margin: auto !important;
        }
        @media (max-width: 600px) {
          div.markdown h1, h2, h3 {
            font-size: 1.4em;
          }
          div.markdown img {
            height: 100%;
          }
        }
      `}</style>
    </Grid>
  )
}

ProductCustomerReviewTemplate.propTypes = {
  classes: PropTypes.object.isRequired,
  theme: PropTypes.object.isRequired,
  data: PropTypes.object.isRequired,
  pathContext: PropTypes.shape({
    reviewBody: PropTypes.string.isRequired,
  }).isRequired,
}

// eslint-disable-next-line
export const query = graphql`
  query CustomerProductReviewQuery($path: String!, $fileAbsolutePath: String!) {
    customerReview: markdownRemark(fields: { slug: { eq: $path } }) {
      html
      excerpt(pruneLength: 300)
      timeToRead
      wordCount {
        words
      }
      fields {
        slug
      }
      frontmatter {
        title
        customerName
        customerAddress
        customerPhoto
        thumbnail
        date
        shortDescription
        tags
        downloads
      }
    }
    file(absolutePath: { eq: $fileAbsolutePath }) {
      modifiedTime
    }
  }
`

export default withStyles(styles, { withTheme: true })(
  ProductCustomerReviewTemplate,
)



// WEBPACK FOOTER //
// ./src/templates/productCustomerReview.js